import React, { useState, useEffect } from 'react';
import './App.css';
import { api } from './api';
import { useInactivityTimer } from './hooks/useInactivityTimer';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import AudioLibrary from './pages/AudioLibrary';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import ChatWidget from './components/ChatWidget';

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [chatAvailable, setChatAvailable] = useState(false);

  const toggleChatAvailability = async () => {
    const newVal = !chatAvailable;
    setChatAvailable(newVal);
    try {
      const token = localStorage.getItem('token');
      await fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/chat/availability', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? 'Bearer ' + token : '' },
        body: JSON.stringify({ available: newVal })
      });
    } catch(_) {}
  };

  // Load initial chat availability
  React.useEffect(() => {
    fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/chat/status')
      .then(r => r.json())
      .then(d => setChatAvailable(d.available))
      .catch(() => {});
  }, []);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    // Check if session expired
    try {
      const expired = localStorage.getItem('sessionExpired');
      if (expired === 'true') {
        setSessionExpired(true);
        localStorage.removeItem('sessionExpired');
      }
    } catch (e) {
      console.error('localStorage not available:', e);
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const userData = await api.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (e) {
        // Ignore localStorage errors in insecure context
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('sessionExpired');
    } catch (e) {
      console.error('localStorage not available:', e);
    }
    setUser(userData);
    setSessionExpired(false);
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (e) {
      // Ignore localStorage errors
    }
    setUser(null);
    setCurrentPage('dashboard');
  };

  // Use inactivity timer hook when user is logged in
  useInactivityTimer(user ? handleLogout : null);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} sessionExpired={sessionExpired} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard user={user} />;
      case 'campaigns':
        return <Campaigns user={user} />;
      case 'audio':
        return <AudioLibrary user={user} />;
      case 'reports':
        return <Reports user={user} />;
      case 'admin':
        return <Admin user={user} />;
      case 'settings':
        return <Settings user={user} />;
      default:
        return <Dashboard user={user} />;
    }
  };

  return (
    <div className="app-container">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>Legend Broadcasting</h1>
          {user.tenant_name && (
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
              {user.tenant_name}
            </p>
          )}
        </div>
        <nav className="sidebar-nav">
          <a
            className={`nav-item ${currentPage === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentPage('dashboard')}
            data-testid="nav-dashboard"
          >
            <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Dashboard
          </a>
          <a
            className={`nav-item ${currentPage === 'campaigns' ? 'active' : ''}`}
            onClick={() => setCurrentPage('campaigns')}
            data-testid="nav-campaigns"
          >
            <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Campaigns
          </a>
          <a
            className={`nav-item ${currentPage === 'audio' ? 'active' : ''}`}
            onClick={() => setCurrentPage('audio')}
            data-testid="nav-audio"
          >
            <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            Audio Library
          </a>
          <a
            className={`nav-item ${currentPage === 'reports' ? 'active' : ''}`}
            onClick={() => setCurrentPage('reports')}
            data-testid="nav-reports"
          >
            <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Reports
          </a>
          {user.role === 'super_admin' && (
            <a
              className={`nav-item ${currentPage === 'admin' ? 'active' : ''}`}
              onClick={() => setCurrentPage('admin')}
              data-testid="nav-admin"
            >
              <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Admin
            </a>
          )}
          <a
            className={`nav-item ${currentPage === 'settings' ? 'active' : ''}`}
            onClick={() => setCurrentPage('settings')}
            data-testid="nav-settings"
          >
            <svg className="nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </a>
        </nav>
        <div style={{ padding: '1rem', borderTop: '1px solid hsl(var(--border))', marginTop: 'auto' }}>
          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
            {user.email}
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleLogout}
            style={{ width: '100%' }}
            data-testid="btn-logout"
          >
            Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="topbar">
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {currentPage.charAt(0).toUpperCase() + currentPage.slice(1)}
          </h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {user && user.role === 'super_admin' && <button
              onClick={toggleChatAvailability}
              title={chatAvailable ? 'Chat: Online — click to go offline' : 'Chat: Offline — click to go online'}
              style={{
                display:'flex', alignItems:'center', gap:6, background:'none',
                border:'1px solid', borderColor: chatAvailable ? '#22c55e' : '#ef4444',
                borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.75rem',
                color: chatAvailable ? '#22c55e' : '#ef4444', fontWeight:500,
              }}
            >
              <span style={{ width:8, height:8, borderRadius:'50%', background: chatAvailable ? '#22c55e' : '#ef4444', display:'inline-block', boxShadow: chatAvailable ? '0 0 5px #22c55e' : 'none' }} />
              {chatAvailable ? 'Chat: Online' : 'Chat: Offline'}
            </button>}
            <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
              {user.role === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}
            </span>
          </div>
        </div>
        <div className="content">
          {renderPage()}
        </div>
      </main>
      <ChatWidget />
    </div>
  );
}

export default App;
